/**
 * Application configuration using Vite environment variables
 *
 * All config values are loaded from environment variables at build time.
 * For local development, use .env.local
 * For CI/CD, environment variables are set in GitHub Actions secrets
 */

interface AppConfig {
  // Environment flags
  IS_INTEGRATION: boolean;
  IS_PRODUCTION: boolean;
  NODE_ENV: string;
  __CLIENT__: boolean;
  __DEVELOPMENT__: boolean;
  __DEVTOOLS__: boolean;

  // Auth0
  AUTH0_CLIENT_ID: string;
  AUTH0_DOMAIN: string;
  AUTH0_AUDIENCE: string;

  // Application URLs
  HOME_URL: string;
  API_URL: string;
  DATA_API_URL: string;
  EXT_DATA_API_URL: string;

  // File Transfer
  FTP_URL: string;
  FTP_DIR: string;
  BULK_UPLOAD_UI_URL: string;

  // API Endpoints
  PS: string;
  TRANS_API_URL: string;
  SF_AGENT_API_URL: string;
  MONITOR_API_URL: string;
  TEST_CONN_API_URL: string;
  RECIPE_MONITORING_API_URL: string;

  // Third-party Services
  GOOGLE_CAPTCHA_KEY: string;
  META_SCORE_UI_URL: string;
  ELEVIO_ACCOUNT_ID: string;
  INTERCOM_APP_ID: string;
  GA_TRACKING_ID: string;

  // CloudWatch RUM
  RUM_ENABLED: boolean;
  SESSION_REPLAY_ENABLED: boolean;
  RUM_APP_ID: string;
  RUM_APP_MONITOR_NAME: string;
  RUM_IDENTITY_POOL_ID: string;
  RUM_REGION: string;
}

const config: AppConfig = {
  // Environment detection
  // Development mode is treated as integration (matches legacy dataProtect development.js: IS_INTEGRATION: true)
  // This ensures forIntegrationOnly connectors (e.g. DocusignPrefill) appear in local dev
  IS_INTEGRATION:
    import.meta.env.DEV ||
    import.meta.env.MODE === 'integration' ||
    import.meta.env.VITE_ENV === 'integration',
  IS_PRODUCTION: import.meta.env.MODE === 'production' || import.meta.env.VITE_ENV === 'production',
  NODE_ENV: import.meta.env.MODE || 'development',

  __CLIENT__: true,
  __DEVELOPMENT__: import.meta.env.DEV,
  __DEVTOOLS__: import.meta.env.DEV,

  // Auth0 Configuration
  AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
  AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN || '',
  AUTH0_AUDIENCE: import.meta.env.VITE_AUTH0_AUDIENCE || '',

  // Application URLs
  HOME_URL: import.meta.env.VITE_HOME_URL || '',
  API_URL: import.meta.env.VITE_API_URL || '',
  DATA_API_URL: import.meta.env.VITE_DATA_API_URL || '',
  EXT_DATA_API_URL: import.meta.env.VITE_EXT_DATA_API_URL || '',

  // File Transfer
  FTP_URL: import.meta.env.VITE_FTP_URL || '',
  FTP_DIR: import.meta.env.VITE_FTP_DIR || '',
  BULK_UPLOAD_UI_URL: import.meta.env.VITE_BULK_UPLOAD_UI_URL || '',

  // API Endpoints
  PS: import.meta.env.VITE_PS || '',
  TRANS_API_URL: import.meta.env.VITE_TRANS_API_URL || '',
  SF_AGENT_API_URL: import.meta.env.VITE_SF_AGENT_API_URL || '',
  MONITOR_API_URL: import.meta.env.VITE_MONITOR_API_URL || '',
  TEST_CONN_API_URL: import.meta.env.VITE_TEST_CONN_API_URL || '',
  RECIPE_MONITORING_API_URL: import.meta.env.VITE_RECIPE_MONITORING_API_URL || '',

  // Third-party Services
  GOOGLE_CAPTCHA_KEY: import.meta.env.VITE_GOOGLE_CAPTCHA_KEY || '',
  META_SCORE_UI_URL: import.meta.env.VITE_META_SCORE_UI_URL || '',
  ELEVIO_ACCOUNT_ID: import.meta.env.VITE_ELEVIO_ACCOUNT_ID || '',
  INTERCOM_APP_ID: import.meta.env.VITE_INTERCOM_APP_ID || '',
  GA_TRACKING_ID: import.meta.env.VITE_GA_TRACKING_ID || '',

  // CloudWatch RUM — disabled unless VITE_RUM_ENABLED=true at build time
  RUM_ENABLED: import.meta.env.VITE_RUM_ENABLED === 'true',
  SESSION_REPLAY_ENABLED: import.meta.env.VITE_SESSION_REPLAY_ENABLED === 'true',
  RUM_APP_ID: import.meta.env.VITE_RUM_APP_ID || '',
  RUM_APP_MONITOR_NAME: import.meta.env.VITE_RUM_APP_MONITOR_NAME || '',
  RUM_IDENTITY_POOL_ID: import.meta.env.VITE_RUM_IDENTITY_POOL_ID || '',
  RUM_REGION: import.meta.env.VITE_RUM_REGION || 'us-east-1',
};

export default config;
